import { useState } from "react";
import { z } from "zod";
import { Link } from "react-router-dom";
import { Sparkles, Megaphone, Users, Trophy, ArrowRight, PlayCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import heroAsset from "@/assets/become-athlete-hero.png.asset.json";
const heroImg = heroAsset.url;
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const signupSchema = z.object({
  full_name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  sport: z.string().trim().max(100).optional().or(z.literal("")),
  team: z.string().trim().max(100).optional().or(z.literal("")),
  league: z.string().trim().max(100).optional().or(z.literal("")),
  level: z.string().trim().max(100).optional().or(z.literal("")),
  social_handle: z.string().trim().max(100).optional().or(z.literal("")),
  message: z.string().trim().max(1000).optional().or(z.literal("")),
});

const BecomeAthlete = () => {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    sport: "",
    team: "",
    league: "",
    level: "",
    social_handle: "",
    message: "",
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consent) {
      toast({ title: "Consent required", description: "Please agree to the consent statement to continue.", variant: "destructive" });
      return;
    }
    const parsed = signupSchema.safeParse(form);
    if (!parsed.success) {
      toast({ title: "Check your details", description: parsed.error.issues[0].message, variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("athlete_signups").insert({
      full_name: parsed.data.full_name,
      email: parsed.data.email,
      sport: parsed.data.sport || null,
      team: parsed.data.team || null,
      league: parsed.data.league || null,
      level: parsed.data.level || null,
      social_handle: parsed.data.social_handle || null,
      message: parsed.data.message || null,
    });
    setSubmitting(false);
    if (error) {
      toast({ title: "Something went wrong", description: error.message, variant: "destructive" });
      return;
    }
    setSubmitted(true);
    toast({ title: "You're in!", description: "Welcome to the SIIS Athlete Network." });
    setForm({ full_name: "", email: "", sport: "", team: "", league: "", level: "", social_handle: "", message: "" });
    setConsent(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* HERO */}
        <section className="relative overflow-hidden">
          <div className="container grid lg:grid-cols-12 gap-8 pt-12 pb-20 lg:pt-20 lg:pb-32">
            <div className="lg:col-span-7 flex flex-col justify-center animate-float-up">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-6">
                <span className="w-8 h-px bg-primary" /> Become a SIIS Athlete
              </span>
              <h1 className="text-[clamp(2.5rem,8vw,7rem)] text-balance">
                Your <span className="serif font-normal text-primary">story</span> matters.
                <br />
                Your <span className="serif font-normal text-accent">voice</span> matters.
                <br />
                Your <span className="serif font-normal text-secondary">future</span> matters.
              </h1>
              <p className="mt-8 text-lg md:text-xl text-muted-foreground max-w-xl">
                Say It In Sports is a platform built to help women athletes grow their visibility,
                build their personal brand, and create opportunities beyond the game.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="#video"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-secondary text-secondary-foreground font-semibold hover:bg-primary hover:text-primary-foreground transition-all shadow-soft"
                >
                  <PlayCircle className="w-5 h-5" /> Watch the video
                </a>
                <a
                  href="#join"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary hover:text-secondary-foreground transition-all shadow-bold"
                >
                  Join the athlete network <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative aspect-[3/4] rounded-[2rem] overflow-hidden shadow-bold">
                <img
                  src={heroImg}
                  alt="Confident woman athlete representing the SIIS Athlete Network"
                  width={1152}
                  height={1536}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/70 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6 text-primary-foreground">
                  <p className="text-xs uppercase tracking-[0.2em] opacity-80">Athlete Network</p>
                  <p className="font-display text-3xl mt-1">Built for women athletes</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FOUNDER VIDEO */}
        <section id="video" className="bg-muted/40 py-20 lg:py-28">
          <div className="container max-w-5xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-4">
              <span className="w-8 h-px bg-primary" /> Founder video
            </span>
            <h2 className="text-4xl md:text-6xl mb-6">A platform built for women athletes.</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mb-10">
              Hear directly from founder Jen Crompton about why Say It In Sports was created — and
              how we're helping athletes amplify their voice, tell their story, and create new
              opportunities on and off the field.
            </p>
            <div className="aspect-video rounded-2xl overflow-hidden bg-secondary/90 shadow-bold flex items-center justify-center">
              <div className="flex flex-col items-center text-secondary-foreground/80">
                <PlayCircle className="w-16 h-16 mb-3" />
                <p className="text-sm uppercase tracking-[0.2em]">Founder video coming soon</p>
              </div>
            </div>
          </div>
        </section>

        {/* WHAT IS SIIS */}
        <section className="py-20 lg:py-28">
          <div className="container max-w-4xl">
            <h2 className="text-4xl md:text-6xl mb-8">
              What is <span className="serif font-normal text-primary">Say It In Sports?</span>
            </h2>
            <div className="space-y-6 text-lg text-muted-foreground">
              <p>
                Say It In Sports is an athlete-first platform focused on visibility, storytelling,
                leadership, and opportunity for women athletes.
              </p>
              <p className="text-foreground font-medium">
                We believe athletes deserve more than highlights and box scores.
              </p>
              <p>
                They deserve platforms.
                <br />
                Representation.
                <br />
                Ownership of their story.
                <br />
                And access to opportunities that extend beyond the game itself.
              </p>
              <p>
                Whether you're looking to grow your personal brand, share your journey, connect
                with brands, or become part of a larger movement in women's sports — this community
                was built for you.
              </p>
            </div>
          </div>
        </section>

        {/* WHAT ATHLETES RECEIVE */}
        <section className="bg-secondary text-secondary-foreground py-20 lg:py-28">
          <div className="container">
            <h2 className="text-4xl md:text-6xl mb-12 text-center">What athletes receive</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Megaphone, title: "Visibility", body: "Athlete spotlights, interviews, features, and storytelling opportunities designed to help your voice reach wider audiences." },
                { icon: Sparkles, title: "Personal Brand Support", body: "Guidance around messaging, profile development, media presence (and media training), and building your platform as an athlete and leader." },
                { icon: Users, title: "Community", body: "A growing network of women athletes, advocates, brands, leagues, and supporters invested in the future of women's sports." },
                { icon: Trophy, title: "Future Opportunities", body: "Potential access to partnerships, collaborations, speaking opportunities, campaigns, media features, and more." },
              ].map(({ icon: Icon, title, body }) => (
                <div key={title} className="bg-background/5 border border-background/10 rounded-2xl p-6 hover:bg-background/10 transition-colors">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-display text-2xl mb-3">{title}</h3>
                  <p className="text-sm opacity-80 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* BUILT WITH ATHLETES */}
        <section className="py-20 lg:py-28">
          <div className="container max-w-4xl">
            <h2 className="text-4xl md:text-6xl mb-8">
              Built with athletes — <span className="serif font-normal text-primary">not around them.</span>
            </h2>
            <div className="space-y-6 text-lg text-muted-foreground">
              <p>
                Too often, women athletes are expected to perform at the highest level without
                receiving the visibility, support, infrastructure, or storytelling investment they
                deserve.
              </p>
              <p className="text-foreground font-medium">We're working to help change that.</p>
              <p>
                Say It In Sports exists to amplify women athletes in a way that feels authentic,
                empowering, and athlete-first.
              </p>
            </div>
          </div>
        </section>

        {/* JOIN FORM */}
        <section id="join" className="bg-muted/40 py-20 lg:py-28">
          <div className="container max-w-3xl">
            <h2 className="text-4xl md:text-6xl mb-6">Join the athlete network</h2>
            <p className="text-lg text-muted-foreground mb-4">
              We're currently onboarding founding athletes to help shape the future of the platform.
            </p>
            <p className="text-base text-muted-foreground mb-2">
              Complete the form below to join the network and receive:
            </p>
            <ul className="list-disc pl-5 text-muted-foreground mb-10 space-y-1">
              <li>Early access to athlete profiles</li>
              <li>Community updates</li>
              <li>Feature opportunities</li>
              <li>Partnership announcements</li>
              <li>Platform news and next steps</li>
            </ul>

            {submitted ? (
              <div className="bg-background border border-border rounded-2xl p-8 shadow-card text-center">
                <h3 className="font-display text-3xl mb-3 text-primary">Welcome to the network!</h3>
                <p className="text-muted-foreground">
                  Keep an eye on your inbox — we'll be in touch with next steps soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-background border border-border rounded-2xl p-6 md:p-8 shadow-card space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="full_name">Full name *</label>
                    <input id="full_name" name="full_name" required maxLength={100} value={form.full_name} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="email">Email *</label>
                    <input id="email" name="email" type="email" required maxLength={255} value={form.email} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="sport">Sport</label>
                    <input id="sport" name="sport" maxLength={100} value={form.sport} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="team">Team</label>
                    <input id="team" name="team" maxLength={100} value={form.team} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="league">League</label>
                    <input id="league" name="league" maxLength={100} value={form.league} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" htmlFor="level">Level (HS, College, Pro, etc.)</label>
                    <input id="level" name="level" maxLength={100} value={form.level} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" htmlFor="social_handle">Instagram / social handle</label>
                  <input id="social_handle" name="social_handle" maxLength={100} value={form.social_handle} onChange={handleChange} placeholder="@yourhandle" className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2" htmlFor="message">Tell us about yourself</label>
                  <textarea id="message" name="message" maxLength={1000} rows={4} value={form.message} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-y" />
                </div>
                <label className="flex items-start gap-3 p-4 rounded-lg border border-border bg-muted/30 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-input accent-primary shrink-0"
                    required
                  />
                  <span className="text-sm text-muted-foreground leading-relaxed">
                    By submitting this form, you give Say It In Sports permission to create a profile on sayitinsports.app featuring your name, sport, team, and the information you provide. You retain full ownership of your story and can request changes or removal at any time.
                  </span>
                </label>
                <button type="submit" disabled={submitting || !consent} className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-secondary hover:text-secondary-foreground transition-all shadow-bold disabled:opacity-60">
                  {submitting ? "Submitting…" : "Join the Athlete Network"} <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            )}
          </div>
        </section>

        {/* WHAT HAPPENS NEXT */}
        <section className="py-20 lg:py-28">
          <div className="container max-w-5xl">
            <h2 className="text-4xl md:text-6xl mb-12">What happens next?</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { n: "1", title: "Welcome Email", body: "You'll receive a welcome email with additional information about the platform and upcoming opportunities." },
                { n: "2", title: "Athlete Profile Access", body: "We'll share instructions for creating and managing your athlete profile." },
                { n: "3", title: "Future Features + Opportunities", body: "As the platform grows, athletes may be selected for interviews, spotlights, partnerships, campaigns, and other initiatives." },
              ].map((step) => (
                <div key={step.n} className="border border-border rounded-2xl p-6 bg-card">
                  <div className="font-display text-5xl text-primary mb-3">{step.n}</div>
                  <h3 className="font-display text-2xl mb-3">{step.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{step.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="bg-gradient-hero text-primary-foreground py-24">
          <div className="container max-w-3xl text-center">
            <h2 className="text-4xl md:text-6xl mb-6">
              Women athletes deserve to be <span className="serif font-normal">seen, heard, and valued.</span>
            </h2>
            <p className="text-lg opacity-90 mb-10">
              Join us as we help build the future of women's sports storytelling and athlete visibility.
            </p>
            <a href="#join" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-background text-foreground font-semibold hover:bg-highlight hover:text-highlight-foreground transition-all shadow-bold">
              Join now <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BecomeAthlete;
